import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import api from '../utils/api';

/**
 * Account module licensing hook (WP-FE.2).
 *
 * Fetches the BFF route `GET /api/v1/account/modules` (WP-BFF.2), which
 * derives the account/tenant/user from the JWT/headers — the frontend
 * passes NO params. The response declares which product modules
 * (`qualificacao`, `crm`) + premium add-ons (`autopilot`) the account is
 * licensed for.
 *
 * Wire shape: the BFF emits `{ modules: { qualification, crm, autopilot } }`
 * — an OBJECT map with camelCase/English keys. `normaliseModules` reads that
 * shape and aliases the divergent `qualification` → the frontend gate
 * vocabulary `qualificacao`. A legacy `{ modules: [{key, licensed}] }` array
 * shape is still accepted defensively (older/alternate BFF builds).
 *
 * This is a DISPLAY gate only — it hides nav entries for non-licensed
 * modules. It is NEVER the source of truth for authorization; the real
 * gate lives in the backend (WP-0.2; for Autopilot, the sales-api runtime
 * entitlement gate). Therefore the hook FAILS OPEN: while the query is
 * loading OR on any failure (404/5xx/timeout/parse-fail),
 * `isModuleLicensed()` returns `true` for every module so nothing is ever
 * hidden by accident. NB: the premium `autopilot` add-on is failed CLOSED at
 * the BFF (it returns `autopilot:false` on a billing glitch), so a genuine
 * "not entitled" answer still hides the menu — only a total /account/modules
 * outage (this hook's own query failing) fails open.
 *
 * Cache strategy mirrors `useCatalog`:
 *   - staleTime 5 min, gcTime 30 min, retry 1.
 *   - The service swallows failures silently (like `fetchCatalog`) so the
 *     query never surfaces an error to the user; the empty fallback map
 *     keeps every module visible via the fail-open `isModuleLicensed`.
 *
 * G9: never logs PII / payloads. G3: error envelopes are never unpacked
 * or displayed here — failures are swallowed.
 */

/** Canonical module keys gated in the UI. Extra keys from the BFF are kept. */
export type ModuleKey = 'qualificacao' | 'crm' | 'autopilot' | (string & {});

/** Normalised map: { [moduleKey]: licensed }. */
export type AccountModules = Record<string, boolean>;

interface RawModule {
  key?: string;
  /** snake_case from the backend; camelCase if a future BFF normalises. */
  licensed?: boolean;
}

interface RawAccountModules {
  /** WP-BFF.2 emits an OBJECT map; a legacy array shape is still accepted. */
  modules?: RawModule[] | Record<string, unknown>;
}

// BFF (WP-BFF.2) key → frontend gate vocabulary. Only `qualification` diverges
// (English on the wire, `qualificacao` in the ModuleKey union); `crm` and
// `autopilot` are identical on both sides.
const KEY_ALIASES: Record<string, string> = { qualification: 'qualificacao' };

function aliasKey(key: string): string {
  return KEY_ALIASES[key] ?? key;
}

function normaliseModules(raw: unknown): AccountModules {
  const map: AccountModules = {};
  if (!raw || typeof raw !== 'object') return map;
  const list = (raw as RawAccountModules).modules;
  if (!list || typeof list !== 'object') return map;

  if (Array.isArray(list)) {
    // Legacy shape: [{ key, licensed }].
    for (const entry of list) {
      if (!entry || typeof entry !== 'object') continue;
      const key = (entry as RawModule).key;
      if (typeof key !== 'string' || key.length === 0) continue;
      map[aliasKey(key)] = Boolean((entry as RawModule).licensed);
    }
    return map;
  }

  // WP-BFF.2 wire shape: { qualification, crm, autopilot } → object map.
  for (const [key, value] of Object.entries(list as Record<string, unknown>)) {
    if (typeof key !== 'string' || key.length === 0) continue;
    map[aliasKey(key)] = value === true;
  }
  return map;
}

async function fetchAccountModules(): Promise<AccountModules> {
  try {
    const { data } = await api.get<RawAccountModules>('/account/modules');
    return normaliseModules(data);
  } catch {
    // Silent swallow — fail-open. The empty map combined with the
    // fail-open `isModuleLicensed` keeps every module visible. We never
    // log the payload/error (G9) nor surface it to the user (G3).
    return {};
  }
}

const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_MINUTES = 30 * 60 * 1000;

export interface UseAccountModulesResult {
  modules: AccountModules;
  /**
   * Fail-open module check. Returns `true` while loading, on error, or for
   * any module not present in the (normalised) payload; returns the
   * declared `licensed` flag only when the module is explicitly present.
   */
  isModuleLicensed: (key: ModuleKey) => boolean;
  isLoading: boolean;
  isError: boolean;
}

export function useAccountModules(): UseAccountModulesResult {
  const result: UseQueryResult<AccountModules> = useQuery<AccountModules>({
    queryKey: ['account', 'modules'],
    queryFn: fetchAccountModules,
    staleTime: FIVE_MINUTES,
    gcTime: THIRTY_MINUTES,
    retry: 1,
  });

  const modules = result.data ?? {};
  const failOpen = result.isLoading || result.isError;

  const isModuleLicensed = (key: ModuleKey): boolean => {
    // Fail-open while loading / on error → everything visible.
    if (failOpen) return true;
    // Explicit flag wins; unknown modules default to visible (fail-open).
    return key in modules ? modules[key] : true;
  };

  return {
    modules,
    isModuleLicensed,
    isLoading: result.isLoading,
    isError: result.isError,
  };
}
